import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { ButtonIcon, IconInfo, Dialog, Box, Columns, Column, Text, IconChevron, TooltipRenderer, IconClear, TextField, IconSearch, Inline } from "braid-design-system";
import React, { useState, useRef, useCallback, useEffect } from "react";
import RenderIfVisible from "react-render-if-visible";
import { translations } from "../../../components/ClickableTooltip/.vocab/index.mjs";
import { DisclosureDialog } from "../DisclosureDialog/DisclosureDialog.mjs";
import { translations as translations$1 } from "../../../components/MultiSelectDropdown/.vocab/index.mjs";
import { MultiSelectDropdownOptionItem } from "./MultiSelectDropdownOptionItem.mjs";
import { clearButtonZIndex, optionsList } from "./MultiSelectDropdown.css.mjs";
const normalizeKey = ({
  key,
  keyCode
}) => {
  if (keyCode >= 37 && keyCode <= 40 && key.indexOf("Arrow") !== 0) {
    return `Arrow${key}`;
  }
  if (keyCode === 27) {
    return "Escape";
  }
  return key;
};
const caseInsensitiveIncludes = (a, b) => a.trim().toLocaleLowerCase().includes(b.trim().toLocaleLowerCase());
const ClickableTooltip = ({
  id,
  title,
  contents,
  size = "small"
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ButtonIcon,
      {
        id: `tooltip-trigger-${id}`,
        data: { testid: `tooltip-trigger-${id}` },
        label: t("Click to learn more"),
        icon: /* @__PURE__ */ jsx(IconInfo, {}),
        size,
        variant: "transparent",
        bleed: true,
        onClick: () => {
          setOpen(true);
        }
      }
    ),
    open ? /* @__PURE__ */ jsx(
      Dialog,
      {
        id: `tooltip-dialog-${id}`,
        title,
        open: true,
        closeLabel: t("Close"),
        onClose: () => {
          setOpen(false);
        },
        children: contents
      }
    ) : null
  ] });
};
const searchForMatchingOption = (option, search) => {
  var _a;
  const foundOption = (_a = option == null ? void 0 : option.children) == null ? void 0 : _a.find(
    (childOption) => caseInsensitiveIncludes(childOption.value, search) || childOption.children && searchForMatchingOption(childOption, search)
  );
  return foundOption;
};
const getFilteredOptionsKeys = (search = "", option, addAll, filteredOptionKeys) => {
  var _a;
  if (caseInsensitiveIncludes(option.value, search) || addAll) {
    filteredOptionKeys.push(option.key);
    if (option.children) {
      option.children.forEach((child) => {
        getFilteredOptionsKeys(search, child, true, filteredOptionKeys);
      });
    }
  } else if (searchForMatchingOption(option, search)) {
    filteredOptionKeys.push(option.key);
    (_a = option == null ? void 0 : option.children) == null ? void 0 : _a.map((childOption) => {
      getFilteredOptionsKeys(search, childOption, addAll, filteredOptionKeys);
    });
  }
};
const findFilteredOptionsKeys = (search, options) => {
  const filteredOptionKeys = [];
  options.map(
    (option) => getFilteredOptionsKeys(search, option, false, filteredOptionKeys)
  );
  return filteredOptionKeys;
};
const allLeafOptionsChecked = (option, selected) => {
  var _a;
  return (_a = option.leafNodeKeys) == null ? void 0 : _a.every((leafNodeKey) => selected.includes(leafNodeKey));
};
const someLeafOptionsChecked = (option, selected) => {
  var _a;
  return (_a = option.leafNodeKeys) == null ? void 0 : _a.find((leafNodeKey) => selected.includes(leafNodeKey));
};
const getCheckboxStatus = (option, selected) => {
  if (!option.leafNodeKeys) {
    return selected.includes(option.key);
  }
  if (allLeafOptionsChecked(option, selected)) {
    return true;
  }
  if (someLeafOptionsChecked(option, selected)) {
    return "mixed";
  }
  return false;
};
const defaultMultiSelectDropdownContext = {
  search: "",
  onSelect: () => {
  },
  selected: [],
  filteredOptionKeys: [],
  onHoverOptionKey: "",
  refocus: () => {
  },
  mouseEnterHandler: () => {
  },
  mouseLeaveHandler: () => {
  },
  isMultipleLevel: false,
  expandedKeys: [],
  expandCollapseHandler: () => {
  }
};
const MultiSelectDropdownContext = React.createContext(
  defaultMultiSelectDropdownContext
);
const MultiSelectDropdown = ({
  disabled = false,
  options,
  selected,
  value,
  valueRenderer,
  onSelect,
  isMultipleLevel = false,
  label,
  searchable = true,
  searchPlaceholder,
  lazyLoadingEnabled = false,
  labelTooltip,
  tooltipProps,
  isNvlEnabled = false,
  disclaimer,
  clearSelection
}) => {
  const searchElementRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setsearch] = useState("");
  const [onHoverOptionKey, setOnHoverOptionKey] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const { t } = useTranslations(translations$1);
  const dropdownState = {
    search,
    expandedKeys: [...expandedKeys],
    isSearchFilled: Boolean(search),
    areOptionsExpanded: Boolean(expandedKeys.length)
  };
  const filteredOptions = !isMultipleLevel ? options.filter((option) => caseInsensitiveIncludes(option.value, search)) : options;
  const filteredOptionKeys = findFilteredOptionsKeys(search, filteredOptions);
  const hoverOption = (indexOperation) => {
    if (onHoverOptionKey) {
      const hoverOptionKeyIndex = filteredOptionKeys.findIndex(
        (optionKey) => optionKey === onHoverOptionKey
      );
      if (hoverOptionKeyIndex === -1) {
        setOnHoverOptionKey(filteredOptionKeys[0]);
      }
      const newIndex = hoverOptionKeyIndex + indexOperation;
      if (newIndex === -1 || newIndex === filteredOptionKeys.length) {
        return;
      }
      setOnHoverOptionKey(
        filteredOptionKeys[hoverOptionKeyIndex + indexOperation]
      );
    } else if (indexOperation === 1) {
      setOnHoverOptionKey(filteredOptionKeys[0]);
    }
  };
  const clearFunction = (e) => {
    clearSelection == null ? void 0 : clearSelection();
    e.stopPropagation();
  };
  const expandCollapseHandler = (key) => {
    setExpandedKeys(
      (current) => current.includes(key) ? [...current].filter((singleOptionKey) => singleOptionKey !== key) : [...current, key]
    );
  };
  const mouseEnterHandler = (key) => {
    setOnHoverOptionKey(key);
  };
  const mouseLeaveHandler = () => {
    setOnHoverOptionKey("");
  };
  const searchElementProps = {
    onChange: (e) => {
      const inputValue = e.target.value;
      setsearch(inputValue);
    },
    onFocus: () => {
      setIsOpen(true);
    },
    onKeyDown: (event) => {
      const targetKey = normalizeKey(event);
      switch (targetKey) {
        case "ArrowDown": {
          event.preventDefault();
          hoverOption(1);
          return;
        }
        case "ArrowUp": {
          event.preventDefault();
          hoverOption(-1);
          return;
        }
        case "Escape": {
          event.preventDefault();
          setOnHoverOptionKey("");
          return;
        }
        case "Enter": {
          if (onHoverOptionKey) {
            event.preventDefault();
            onSelect(onHoverOptionKey, dropdownState);
          }
          return;
        }
        default: {
          return;
        }
      }
    }
  };
  const onToggle = () => {
    setIsOpen((current) => !current);
  };
  const onBackdropClicked = useCallback(() => {
    setsearch("");
    setIsOpen(false);
  }, []);
  const refocus = () => {
    var _a;
    (_a = searchElementRef.current) == null ? void 0 : _a.focus();
  };
  const onSelectHandler = (selectedValue) => {
    onSelect(selectedValue, dropdownState);
    refocus();
  };
  const getLabelElement = () => {
    if (label && labelTooltip) {
      return /* @__PURE__ */ jsx(Box, { paddingBottom: "xsmall", children: /* @__PURE__ */ jsx(DisclosureDialog, { tooltip: labelTooltip, children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", children: label }) }) });
    }
    if (label && tooltipProps) {
      return /* @__PURE__ */ jsx(Box, { paddingBottom: "xsmall", children: /* @__PURE__ */ jsxs(Inline, { space: "xsmall", alignY: "center", children: [
        /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", maxLines: 1, children: label }),
        /* @__PURE__ */ jsx(
          ClickableTooltip,
          {
            id: tooltipProps.id,
            title: tooltipProps.title ?? label,
            contents: tooltipProps.contents
          }
        )
      ] }) });
    }
    if (label) {
      return /* @__PURE__ */ jsx(Box, { paddingBottom: "xsmall", children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", children: label }) });
    }
    return null;
  };
  useEffect(() => {
    var _a;
    if (isOpen) {
      refocus();
    } else {
      (_a = searchElementRef.current) == null ? void 0 : _a.blur();
    }
  }, [isOpen]);
  const cursor = disabled ? "default" : "pointer";
  const borderStyle = disabled ? "borderNeutralLight" : "borderField";
  return /* @__PURE__ */ jsx(
    MultiSelectDropdownContext.Provider,
    {
      value: {
        search,
        onSelect: onSelectHandler,
        refocus,
        mouseEnterHandler,
        mouseLeaveHandler,
        onHoverOptionKey,
        filteredOptionKeys,
        selected,
        isMultipleLevel,
        expandedKeys,
        expandCollapseHandler
      },
      children: /* @__PURE__ */ jsxs(Box, { "aria-label": label, role: "combobox", "data-id": "component-multi-select", children: [
        getLabelElement(),
        /* @__PURE__ */ jsx(
          Box,
          {
            borderRadius: "standard",
            boxShadow: isNvlEnabled ? borderStyle : "borderNeutralLight",
            paddingX: isNvlEnabled ? "small" : "medium",
            paddingY: "small",
            onClick: disabled ? void 0 : onToggle,
            cursor,
            width: "full",
            background: disabled ? "neutralLight" : "surface",
            children: /* @__PURE__ */ jsxs(Columns, { alignY: "center", space: "xsmall", children: [
              /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
                Box,
                {
                  position: "relative",
                  background: "surface",
                  display: "flex",
                  title: value,
                  width: "full",
                  children: /* @__PURE__ */ jsx(
                    Box,
                    {
                      component: "button",
                      "data-id": "component-multi-select-panel-trigger",
                      display: "block",
                      outline: "none",
                      width: "full",
                      value,
                      cursor,
                      textAlign: "left",
                      background: disabled ? "neutralLight" : "surface",
                      children: /* @__PURE__ */ jsx(
                        Text,
                        {
                          maxLines: 1,
                          tone: isNvlEnabled ? "secondary" : void 0,
                          children: value
                        }
                      )
                    }
                  )
                }
              ) }),
              !disabled ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { pointerEvents: "none", cursor: "pointer", height: "full", children: /* @__PURE__ */ jsx(
                IconChevron,
                {
                  tone: "secondary",
                  size: isNvlEnabled ? "xsmall" : "standard",
                  direction: isOpen ? "up" : "down"
                }
              ) }) }) : /* @__PURE__ */ jsx(
                Box,
                {
                  paddingBottom: isNvlEnabled ? "xsmall" : "small",
                  paddingTop: isNvlEnabled ? "xsmall" : "small"
                }
              ),
              clearSelection && selected.length ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
                TooltipRenderer,
                {
                  id: "clear-selection-tooltip",
                  tooltip: /* @__PURE__ */ jsx(Text, { children: t("Clear selection") }),
                  children: ({ triggerProps }) => /* @__PURE__ */ jsx(
                    Box,
                    {
                      position: "relative",
                      onClick: clearFunction,
                      role: "button",
                      data: { testid: "clear-selection-button" },
                      className: clearButtonZIndex,
                      ...triggerProps,
                      children: /* @__PURE__ */ jsx(IconClear, { tone: "secondary", size: "small" })
                    }
                  )
                }
              ) }) : null
            ] })
          }
        ),
        isOpen && /* @__PURE__ */ jsx(
          Box,
          {
            bottom: 0,
            cursor: "default",
            display: "block",
            left: 0,
            position: "fixed",
            right: 0,
            top: 0,
            zIndex: "dropdownBackdrop",
            onClick: onBackdropClicked,
            "data-id": "dropdown-backdrop"
          }
        ),
        isOpen && /* @__PURE__ */ jsx(Box, { position: "relative", zIndex: "dropdown", width: "full", children: /* @__PURE__ */ jsxs(
          Box,
          {
            "data-id": "multi-select-dropdown",
            background: "surface",
            marginTop: "xxsmall",
            position: "absolute",
            borderRadius: "standard",
            width: "full",
            boxShadow: "medium",
            children: [
              searchable && /* @__PURE__ */ jsx(
                Box,
                {
                  "data-id": "searchbar",
                  background: "surface",
                  autoFocus: true,
                  paddingX: "medium",
                  paddingY: "small",
                  borderRadius: "standard",
                  ...searchElementProps,
                  children: /* @__PURE__ */ jsx(
                    TextField,
                    {
                      id: "search",
                      "aria-label": "Search",
                      type: "search",
                      value: search,
                      onChange: searchElementProps.onChange,
                      placeholder: searchPlaceholder,
                      icon: /* @__PURE__ */ jsx(IconSearch, { tone: "secondary" }),
                      ref: searchElementRef
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsxs(
                Box,
                {
                  paddingTop: "xsmall",
                  paddingBottom: "xsmall",
                  className: optionsList,
                  "data-id": "option-list",
                  children: [
                    filteredOptions.length > 0 && filteredOptions.map(
                      (option, index) => lazyLoadingEnabled ? /* @__PURE__ */ jsx(
                        RenderIfVisible,
                        {
                          defaultHeight: 36,
                          children: /* @__PURE__ */ jsx(
                            Box,
                            {
                              id: option.key,
                              paddingX: "small",
                              children: /* @__PURE__ */ jsx(
                                MultiSelectDropdownOptionItem,
                                {
                                  multiSelectOption: option,
                                  leftIndent: isNvlEnabled ? 0 : 8,
                                  isNvlEnabled,
                                  valueRenderer
                                }
                              )
                            },
                            `multi-select-option-${index}`
                          )
                        },
                        `${option.key}-${index}`
                      ) : /* @__PURE__ */ jsx(
                        Box,
                        {
                          id: option.key,
                          paddingX: "small",
                          children: /* @__PURE__ */ jsx(
                            MultiSelectDropdownOptionItem,
                            {
                              multiSelectOption: option,
                              leftIndent: isNvlEnabled ? 0 : 8,
                              isNvlEnabled,
                              valueRenderer
                            }
                          )
                        },
                        `multi-select-option-${index}`
                      )
                    ),
                    (!filteredOptions.length || !filteredOptionKeys.length) && /* @__PURE__ */ jsx(Box, { paddingLeft: "medium", paddingBottom: "small", children: /* @__PURE__ */ jsx(Text, { tone: isNvlEnabled ? "secondary" : void 0, children: t("No matches found.") }) })
                  ]
                }
              ),
              disclaimer ? /* @__PURE__ */ jsx(Box, { padding: "medium", paddingTop: "small", children: disclaimer }) : null
            ]
          }
        ) })
      ] })
    }
  );
};
export {
  MultiSelectDropdown,
  MultiSelectDropdownContext,
  getCheckboxStatus,
  searchForMatchingOption
};
